/*
 * itv-cyber-city.js — the SQL Injection Playground controller.
 *
 * The north star: not "read about SQLi and pick an answer" but "type a payload
 * into a real login form and watch a REAL database let you in." The engine
 * (window.TinySQL, itv-cyber-city-engine.js) genuinely executes the query;
 * authentication bypass EMERGES from the rows returned, it is never compared
 * against a stored answer. That is the whole differentiator versus every
 * string-matching "simulator" — a skeptic can disprove a matcher in ten seconds
 * by typing a payload it was not told about, and here that payload works.
 *
 * No backend. No account. Refresh = pristine DB. View Source reveals the schema
 * on purpose — discovering the schema is what challenge 2 teaches.
 *
 * Progressive enhancement only: the server-rendered lesson content stands on its
 * own if this file never loads. Everything here is guarded so a missing engine
 * degrades to an inert-but-intact page rather than a broken one.
 */
(function () {
  'use strict';

  if (!window.TinySQL) {
    // Engine missing: leave the server-rendered content untouched and readable.
    // Do not throw — a dead console is better than a broken page.
    return;
  }
  var SQL = window.TinySQL;

  // ---- analytics, guarded (see itv-analytics.js). Props are allowlisted to a
  //      fixed five names (source/tier/outcome/count/n); anything else is
  //      silently dropped, so game state is expressed in those keys only. ----
  function track(name, props, once) {
    try {
      if (window.ITVAnalytics && typeof window.ITVAnalytics.track === 'function') {
        window.ITVAnalytics.track(name, props || {}, once ? { once: true } : undefined);
      }
    } catch (e) { /* never let telemetry break the tool */ }
  }

  // ---- the seed database. Deliberately visible in source: schema discovery is
  //      challenge 2, not a secret. The "secrets" table is the exfiltration
  //      target for the UNION challenge. ----
  var SEED = {
    users: {
      columns: ['id', 'username', 'password', 'role', 'email'],
      rows: [
        { id: 1, username: 'admin',  password: 'S3cr3t!Adm-9x', role: 'admin', email: 'admin@citybank.test' },
        { id: 2, username: 'alice',  password: 'spring2026',    role: 'user',  email: 'alice@citybank.test' },
        { id: 3, username: 'bob',    password: 'letmein',       role: 'user',  email: 'bob@citybank.test' },
        { id: 4, username: 'carol',  password: 'hunter2',       role: 'user',  email: 'carol@citybank.test' }
      ]
    },
    secrets: {
      columns: ['id', 'username', 'password', 'role', 'email'],
      rows: [
        { id: 99, username: 'VAULT_PIN', password: '4417-8823-0091', role: 'system', email: 'do-not-share@citybank.test' }
      ]
    },
    // A real SQLite database exposes its schema through sqlite_master — this is
    // exactly how an attacker enumerates tables, so we expose it faithfully
    // rather than faking schema discovery. Five columns, matching the users
    // query, so a UNION lines up.
    sqlite_master: {
      columns: ['type', 'name', 'tbl_name', 'rootpage', 'sql'],
      rows: [
        { type: 'table', name: 'users',   tbl_name: 'users',   rootpage: 2, sql: 'CREATE TABLE users(id, username, password, role, email)' },
        { type: 'table', name: 'secrets', tbl_name: 'secrets', rootpage: 3, sql: 'CREATE TABLE secrets(id, username, password, role, email)' }
      ]
    }
  };

  // ---- the login harness. The vulnerable path concatenates; the fixed path
  //      binds. Both run against the SAME real engine, so the difference the
  //      visitor sees is the difference the vulnerability actually makes. ----
  function buildVulnerableSql(username, password) {
    return "SELECT * FROM users WHERE username = '" + username + "' AND password = '" + password + "'";
  }

  function runVulnerable(username, password) {
    var sql = buildVulnerableSql(username, password);
    var db = SQL.makeDb(SEED);
    try {
      var results = SQL.execute(sql, db);
      var first = results[0];
      var rows = first && first.kind === 'select' ? first.objects : [];
      return { ok: rows.length >= 1, rows: rows, sql: sql, error: null, db: db, results: results };
    } catch (e) {
      return { ok: false, rows: [], sql: sql, error: e.message, db: db, results: null };
    }
  }

  function runParameterised(username, password) {
    // Parameter placeholders — user input can never change the query structure.
    var sql = "SELECT * FROM users WHERE username = ? AND password = ?";
    var db = SQL.makeDb(SEED);
    try {
      var results = SQL.execute(sql, db, [username, password]);
      var first = results[0];
      var rows = first && first.kind === 'select' ? first.objects : [];
      return { ok: rows.length >= 1, rows: rows, sql: sql, error: null, db: db, results: results };
    } catch (e) {
      return { ok: false, rows: [], sql: sql, error: e.message, db: db, results: null };
    }
  }

  // ---- challenge definitions. Each is graded by a PROPERTY of the real result,
  //      never by matching the payload string. `check(res, ctx)` receives the
  //      execution result and returns true when the objective is genuinely met. ----
  var CHALLENGES = [
    {
      id: 'l1-bypass',
      title: 'Bypass the login',
      brief: 'Log in as any user without knowing a password. You only need the WHERE clause to always be true.',
      // Solved iff at least one row came back from a query the visitor did not
      // authenticate legitimately (i.e. rows returned but the typed username is
      // not a real account with the typed password).
      check: function (res) { return res.ok && res.rows.length >= 1; },
      solvedHint: "Put  ' OR '1'='1' --  in the username and leave the password blank. The -- comments out the AND password check; without it, that clause still blocks you."
    },
    {
      id: 'l2-admin',
      title: 'Log in specifically as the admin',
      brief: 'A blanket bypass returns everyone. Return the admin row on its own.',
      check: function (res) { return res.ok && res.rows.length === 1 && res.rows[0].role === 'admin'; },
      solvedHint: "Target the row:  admin' --  comments out the password check."
    },
    {
      id: 'l3-schema',
      title: 'Discover the hidden table',
      brief: 'There is a second table in this database. Use a UNION to read the list of tables and find its name.',
      check: function (res) {
        // Solved when a UNION surfaces a value naming the secrets table.
        return res.ok && res.rows.some(function (r) {
          return Object.keys(r).some(function (k) {
            var v = r[k];
            return typeof v === 'string' && /secrets/i.test(v);
          });
        });
      },
      solvedHint: "' UNION SELECT type, name, tbl_name, rootpage, sql FROM sqlite_master --  lists the tables."
    },
    {
      id: 'l4-exfiltrate',
      title: 'Exfiltrate the vault PIN',
      brief: 'Now dump the secrets table through the login form. Match the five columns of the users query.',
      check: function (res) {
        return res.ok && res.rows.some(function (r) {
          return Object.keys(r).some(function (k) {
            var v = r[k];
            return typeof v === 'string' && v.indexOf('4417-8823-0091') !== -1;
          });
        });
      },
      solvedHint: "' UNION SELECT id, username, password, role, email FROM secrets --"
    },
    {
      id: 'l5-patch',
      title: 'Switch to Blue Team and defend',
      brief: 'Press Blue Team to patch the login, then run your bypass again. Watch the exact same input fail.',
      // Solved when, in parameterised mode, a previously-working bypass now
      // returns zero rows while a legitimate login still succeeds. Driven by the
      // UI toggle rather than a payload.
      check: function () { return false; },
      manual: true,
      solvedHint: "Parameter binding separates code from data, so ' OR '1'='1 is treated as a literal username that simply does not exist."
    }
  ];

  // ---- state ----
  var state = {
    mode: 'vulnerable',      // 'vulnerable' | 'parameterised'
    challengeIndex: 0,
    solved: {},              // id -> true
    miss: {},                // id -> failed-attempt count, for two-stage coaching
    attempts: 0,
    reputation: 0
  };
  try {
    var saved = JSON.parse(localStorage.getItem('itv_cc_progress') || '{}');
    if (saved && saved.solved) { state.solved = saved.solved; state.reputation = saved.reputation || 0; }
  } catch (e) { /* private mode / disabled storage — fine, start fresh */ }

  function persist() {
    try {
      localStorage.setItem('itv_cc_progress', JSON.stringify({ solved: state.solved, reputation: state.reputation }));
    } catch (e) { /* ignore */ }
  }

  // ---- DOM ----
  var $ = function (id) { return document.getElementById(id); };
  var els = {};

  function cacheEls() {
    els.root = $('cc-root');
    els.form = $('cc-login-form');
    els.user = $('cc-username');
    els.pass = $('cc-password');
    els.submit = $('cc-submit');
    els.queryView = $('cc-query-view');
    els.result = $('cc-result');
    els.rows = $('cc-rows');
    els.coach = $('cc-coach');
    els.modeVuln = $('cc-mode-vuln');
    els.modePar = $('cc-mode-par');
    els.modeLabel = $('cc-mode-label');
    els.explainRed = $('cc-explain-red');
    els.explainBlue = $('cc-explain-blue');
    els.challengeTitle = $('cc-challenge-title');
    els.challengeBrief = $('cc-challenge-brief');
    els.challengeList = $('cc-challenge-list');
    els.hintBtn = $('cc-hint-btn');
    els.hint = $('cc-hint');
    els.reputation = $('cc-reputation');
    els.reset = $('cc-reset');
    els.selftest = $('cc-selftest');
    els.selftestOut = $('cc-selftest-out');
    return !!(els.form && els.user && els.pass && els.queryView && els.result && els.rows);
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Render the constructed query with the attacker-controlled portions
  // highlighted INSIDE the developer's quotes — so the visitor sees the
  // quote-breakout happen. Only meaningful in vulnerable mode.
  function renderQuery(username, password) {
    if (state.mode === 'parameterised') {
      els.queryView.innerHTML =
        '<span class="cc-sql-kw">SELECT</span> * <span class="cc-sql-kw">FROM</span> users ' +
        '<span class="cc-sql-kw">WHERE</span> username = <span class="cc-sql-param">?</span> ' +
        '<span class="cc-sql-kw">AND</span> password = <span class="cc-sql-param">?</span>' +
        '<div class="cc-sql-bindings">bindings: [<span class="cc-inj">' + esc(username) +
        '</span>, <span class="cc-inj">' + esc(password) + '</span>]</div>';
      return;
    }
    els.queryView.innerHTML =
      '<span class="cc-sql-kw">SELECT</span> * <span class="cc-sql-kw">FROM</span> users ' +
      '<span class="cc-sql-kw">WHERE</span> username = \'<span class="cc-inj">' + esc(username) +
      '</span>\' <span class="cc-sql-kw">AND</span> password = \'<span class="cc-inj">' + esc(password) + '</span>\'';
  }

  function renderRows(res) {
    if (res.error) {
      els.rows.innerHTML = '<div class="cc-db-error">Database error: ' + esc(res.error) +
        '</div><div class="cc-db-note">A real SQL error &mdash; your input broke the query syntax. ' +
        'That error message is itself information an attacker uses.</div>';
      return;
    }
    if (!res.rows.length) {
      els.rows.innerHTML = '<div class="cc-db-empty">0 rows returned.</div>';
      return;
    }
    var cols = Object.keys(res.rows[0]);
    var html = '<table class="cc-db-table"><thead><tr>';
    cols.forEach(function (c) { html += '<th>' + esc(c) + '</th>'; });
    html += '</tr></thead><tbody>';
    res.rows.forEach(function (r) {
      html += '<tr>';
      cols.forEach(function (c) { html += '<td>' + esc(r[c] == null ? 'NULL' : r[c]) + '</td>'; });
      html += '</tr>';
    });
    html += '</tbody></table>';
    els.rows.innerHTML = html;
  }

  function setResult(res, username, password) {
    var authed = res.ok;
    els.result.className = 'cc-result ' + (authed ? 'cc-result-in' : 'cc-result-out');
    if (res.error) {
      els.result.textContent = 'QUERY ERROR';
    } else if (authed) {
      var who = res.rows.length === 1 ? (' as ' + res.rows[0].username) : (' — ' + res.rows.length + ' accounts matched');
      els.result.textContent = 'ACCESS GRANTED' + who;
    } else {
      els.result.textContent = 'ACCESS DENIED';
    }
  }

  // Distinguish a real login (correct creds) from an injection bypass, so we can
  // report honestly and grade challenges.
  function isLegitLogin(username, password) {
    return SEED.users.rows.some(function (r) {
      return r.username === username && r.password === password;
    });
  }

  function currentChallenge() { return CHALLENGES[state.challengeIndex]; }

  function attempt() {
    var username = els.user.value;
    var password = els.pass.value;
    state.attempts++;

    var res = state.mode === 'parameterised'
      ? runParameterised(username, password)
      : runVulnerable(username, password);

    renderQuery(username, password);
    renderRows(res);
    setResult(res, username, password);

    var legit = isLegitLogin(username, password);
    var bypass = res.ok && !legit && state.mode === 'vulnerable';

    // outcome is one of the allowlisted-friendly scalars
    var outcome = res.error ? 'error' : (bypass ? 'bypassed' : (res.ok ? 'login' : 'denied'));
    track('attempt', { source: currentChallenge().id, outcome: outcome, count: state.attempts });

    gradeChallenges(res, { legit: legit, bypass: bypass, username: username, password: password });
    coach(res, legit);
  }

  // Coach a near-miss instead of leaving the visitor with a bare error. The
  // engine is real (that is the point), but a real error with no guidance stops
  // beginners cold. Two-stage on purpose: the FIRST miss names the mechanism and
  // leaves the "aha" to the player; the SECOND hands over the literal payload so
  // nobody stays stuck. Only while the current attack challenge is unsolved.
  function coach(res, legit) {
    if (!els.coach) return;
    var ch = currentChallenge();
    var miss = res.error || (!res.ok && !legit); // a genuine failed attack
    if (!(state.mode === 'vulnerable' && ch && !ch.manual && !state.solved[ch.id] && miss)) {
      els.coach.hidden = true; els.coach.innerHTML = ''; return;
    }
    state.miss[ch.id] = (state.miss[ch.id] || 0) + 1;
    var second = state.miss[ch.id] >= 2;
    var payload = "<code>' OR '1'='1' --</code>";
    var msg;
    if (res.error && /unterminat|quote|syntax/i.test(res.error)) {
      msg = second
        ? "Your quotes still do not balance. End your input with a  <code>--</code>  comment so the rest of the line is ignored:  " + payload
        : "Your quotes do not balance, so the query is invalid. In SQL, a  <code>--</code>  comment makes everything after it on the line vanish. How could that finish your attack cleanly?";
    } else if (ch.id === 'l1-bypass') {
      msg = second
        ? "The  <code>AND password</code>  check is still blocking you. Comment it out by ending your input with  <code>--</code>&nbsp;:  " + payload
        : "So close &mdash; your condition is true, but the  <code>AND password</code>  check at the end still has to pass. How do you make SQL ignore the rest of the line?";
    } else {
      msg = "Not a match yet. Open  Show hint  for the exact shape of this attack.";
    }
    els.coach.innerHTML = msg;
    els.coach.hidden = false;
  }

  function gradeChallenges(res, ctx) {
    // The manual patch challenge (l5) is graded by the mode toggle handler.
    CHALLENGES.forEach(function (ch, i) {
      if (state.solved[ch.id] || ch.manual) return;
      var ok = false;
      try { ok = ch.check(res, ctx); } catch (e) { ok = false; }
      if (ok) markSolved(ch, i);
    });
    renderChallengeList();
  }

  function markSolved(ch, i) {
    if (state.solved[ch.id]) return;
    state.solved[ch.id] = true;
    state.reputation += 100;
    persist();
    track('challenge_solved', { source: ch.id, outcome: 'solved', count: solvedCount() }, true);
    if (els.reputation) els.reputation.textContent = state.reputation;
    if (els.coach) els.coach.hidden = true; // success speaks for itself; clear any tip
    // advance the spotlight to the next unsolved challenge
    var next = CHALLENGES.findIndex(function (c) { return !state.solved[c.id]; });
    if (next !== -1) { state.challengeIndex = next; renderChallengeSpotlight(); }
    else { track('all_solved', { outcome: 'complete', count: solvedCount() }, true); }
  }

  function solvedCount() {
    return CHALLENGES.filter(function (c) { return state.solved[c.id]; }).length;
  }

  function renderChallengeSpotlight() {
    var ch = currentChallenge();
    if (els.challengeTitle) els.challengeTitle.textContent = ch.title;
    if (els.challengeBrief) els.challengeBrief.textContent = ch.brief;
    if (els.hint) { els.hint.textContent = ch.solvedHint; els.hint.hidden = true; }
  }

  // Progress as a row of dots, not a text list — keeps the panel quiet.
  function renderChallengeList() {
    if (!els.challengeList) return;
    var html = '';
    CHALLENGES.forEach(function (ch, i) {
      var cls = (state.solved[ch.id] ? 'cc-ch-done' : '') + (i === state.challengeIndex ? ' cc-ch-active' : '');
      html += '<li class="' + cls.trim() + '" title="' + esc(ch.title) + (state.solved[ch.id] ? ' (done)' : '') + '"></li>';
    });
    els.challengeList.innerHTML = html;
    if (els.reputation) els.reputation.textContent = state.reputation;
  }

  function setMode(mode) {
    state.mode = mode;
    var vuln = mode === 'vulnerable';
    if (els.modeVuln) els.modeVuln.setAttribute('aria-pressed', String(vuln));
    if (els.modePar) els.modePar.setAttribute('aria-pressed', String(!vuln));
    if (els.modeLabel) {
      els.modeLabel.textContent = vuln
        ? 'ATTACK / vulnerable query'
        : 'DEFENCE / parameterised query';
    }
    // Recolour the whole console to the chosen team — the observable effect the
    // original Red/Blue toggle never had.
    if (els.root) els.root.setAttribute('data-team', vuln ? 'red' : 'blue');
    if (els.form) els.form.setAttribute('data-mode', mode);
    // Both team explanations are server-rendered in the markup (so they exist for
    // a reader and a crawler even if the toggle is never touched); the toggle only
    // switches which is visible — the same pattern as the real-engine claim.
    if (els.explainRed) els.explainRed.hidden = !vuln;
    if (els.explainBlue) els.explainBlue.hidden = vuln;
    // Re-run whatever is in the fields so the change is immediately visible.
    renderQuery(els.user.value, els.pass.value);
    track('mode_change', { source: 'toggle', outcome: mode === 'vulnerable' ? 'vulnerable' : 'patched' });

    // Grade the patch challenge: solved when, in parameterised mode, a bypass
    // that worked in vulnerable mode now returns zero rows AND a legit login
    // still succeeds. We verify both properties live against the engine.
    if (mode === 'parameterised' && !state.solved['l5-patch']) {
      var probeBypass = runParameterised("' OR '1'='1' --", '');
      var probeLegit = runParameterised('admin', 'S3cr3t!Adm-9x');
      if (!probeBypass.ok && probeLegit.ok) {
        // Only credit it once the user has actually landed a bypass earlier,
        // so it rewards understanding rather than toggling on arrival.
        if (state.solved['l1-bypass']) {
          var ch = CHALLENGES.find(function (c) { return c.id === 'l5-patch'; });
          markSolved(ch, CHALLENGES.indexOf(ch));
        }
      }
    }
  }

  // ---- in-page self-test: proves to a skeptical visitor (and to us) that the
  //      engine really executes. Mirrors the canary serp-owner asked for. ----
  function selfTest() {
    var checks = [];
    function ck(name, cond) { checks.push({ name: name, ok: !!cond }); }

    ck('legit admin login succeeds', runVulnerable('admin', 'S3cr3t!Adm-9x').ok);
    ck('wrong password fails', !runVulnerable('admin', 'nope').ok);
    var bypass = runVulnerable("' OR '1'='1' --", '');
    ck("' OR '1'='1' -- bypasses (vulnerable)", bypass.ok);
    ck('same payload FAILS when parameterised', !runParameterised("' OR '1'='1' --", '').ok);
    // random tautology a string-matcher cannot have hardcoded
    var r = Math.random().toString(36).slice(2, 10);
    ck('random tautology bypasses', runVulnerable("x' OR '" + r + "'='" + r + "' --", '').ok);
    ck('random FALSE comparison fails', !runVulnerable("x' OR '" + r + "'='zz" + r + "' --", '').ok);
    var union = runVulnerable("nobody' UNION SELECT id, username, password, role, email FROM secrets --", '');
    ck('UNION exfiltrates the vault PIN', union.rows.some(function (row) {
      return Object.keys(row).some(function (k) { return String(row[k]).indexOf('4417-8823-0091') !== -1; });
    }));

    var passed = checks.filter(function (c) { return c.ok; }).length;
    var html = '<strong>' + passed + '/' + checks.length + ' passed</strong><ul>';
    checks.forEach(function (c) {
      html += '<li class="' + (c.ok ? 'cc-st-ok' : 'cc-st-fail') + '">' + (c.ok ? '✓' : '✗') + ' ' + esc(c.name) + '</li>';
    });
    html += '</ul>';
    if (els.selftestOut) { els.selftestOut.innerHTML = html; els.selftestOut.hidden = false; }
    track('selftest', { source: 'selftest', outcome: passed === checks.length ? 'pass' : 'fail', count: passed });
    return passed === checks.length;
  }

  // ---- deep link: #challenge=l3-schema opens on that challenge, pre-seeding
  //      state for a teacher-shared link. ----
  function applyDeepLink() {
    var m = /challenge=([a-z0-9-]+)/i.exec(location.hash || '');
    if (!m) return;
    var idx = CHALLENGES.findIndex(function (c) { return c.id === m[1]; });
    if (idx !== -1) state.challengeIndex = idx;
  }

  function reset() {
    state.solved = {};
    state.miss = {};
    state.reputation = 0;
    state.attempts = 0;
    state.challengeIndex = 0;
    state.mode = 'vulnerable';
    persist();
    els.user.value = '';
    els.pass.value = '';
    els.rows.innerHTML = '';
    els.result.className = 'cc-result';
    els.result.textContent = '';
    if (els.coach) els.coach.hidden = true;
    setMode('vulnerable');
    renderChallengeSpotlight();
    renderChallengeList();
    renderQuery('', '');
    track('reset', { source: 'reset', outcome: 'reset' });
  }

  function init() {
    if (!cacheEls()) return; // markup not present — nothing to enhance
    applyDeepLink();

    els.form.addEventListener('submit', function (e) { e.preventDefault(); attempt(); });
    if (els.modeVuln) els.modeVuln.addEventListener('click', function () { setMode('vulnerable'); });
    if (els.modePar) els.modePar.addEventListener('click', function () { setMode('parameterised'); });
    if (els.hintBtn) els.hintBtn.addEventListener('click', function () {
      if (els.hint) { els.hint.hidden = !els.hint.hidden; if (!els.hint.hidden) track('hint', { source: currentChallenge().id, outcome: 'shown' }); }
    });
    if (els.reset) els.reset.addEventListener('click', reset);
    if (els.selftest) els.selftest.addEventListener('click', selfTest);

    // live query preview as the user types
    els.user.addEventListener('input', function () { renderQuery(els.user.value, els.pass.value); });
    els.pass.addEventListener('input', function () { renderQuery(els.user.value, els.pass.value); });

    setMode('vulnerable');
    renderChallengeSpotlight();
    renderChallengeList();
    renderQuery('', '');
    if (els.reputation) els.reputation.textContent = state.reputation;
    track('page_ready', { source: 'sqli', outcome: 'ready' }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // expose a tiny surface for verification from the page/console
  window.itvCyberCity = {
    runVulnerable: runVulnerable,
    runParameterised: runParameterised,
    selfTest: selfTest,
    _state: state
  };
})();
